import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useId,
  useReducer,
} from "react";
import axios from "axios";
import { createPortal } from "react-dom";
import { getAnimationVideoUrl } from "./videoSources";
import CrossfadeVideo from "./CrossfadeVideo";

const WEATHER_REFETCH_MS = 5 * 60 * 1000;

/** Server-side proxy (Netlify Function) — no API key in the browser bundle. */
function weatherApiUrl(city) {
  return `/.netlify/functions/weather?city=${encodeURIComponent(city)}`;
}

const LOCAL_TIME_TICK_MS = 2.5 * 60 * 1000;

/** One cache + one poll per city so preview, lightbox, and any duplicate mounts stay in sync. */
const weatherCache = new Map();
const citySubscribers = new Map();
const pollIntervals = new Map();

function notifyCity(city) {
  const subs = citySubscribers.get(city);
  if (subs) subs.forEach((fn) => fn());
}

function subscribeCity(city, onUpdate) {
  if (!citySubscribers.has(city)) citySubscribers.set(city, new Set());
  citySubscribers.get(city).add(onUpdate);
  return () => {
    const set = citySubscribers.get(city);
    if (!set) return;
    set.delete(onUpdate);
    if (set.size === 0) {
      citySubscribers.delete(city);
      const intId = pollIntervals.get(city);
      if (intId != null) {
        clearInterval(intId);
        pollIntervals.delete(city);
      }
    }
  };
}

function ensureCityPolling(city) {
  if (pollIntervals.has(city)) return;
  const run = async () => {
    try {
      const { data } = await axios.get(weatherApiUrl(city));
      weatherCache.set(city, data);
      notifyCity(city);
    } catch {
      // Skip failed poll; next interval retries.
    }
  };
  run();
  pollIntervals.set(city, setInterval(run, WEATHER_REFETCH_MS));
}

let globalTimeIntervalId = null;
const timeTickListeners = new Set();

function notifyTimeTick() {
  timeTickListeners.forEach((fn) => fn());
}

function subscribeTimeTick(onUpdate) {
  timeTickListeners.add(onUpdate);
  if (globalTimeIntervalId == null) {
    globalTimeIntervalId = setInterval(notifyTimeTick, LOCAL_TIME_TICK_MS);
  }
  return () => {
    timeTickListeners.delete(onUpdate);
    if (timeTickListeners.size === 0 && globalTimeIntervalId != null) {
      clearInterval(globalTimeIntervalId);
      globalTimeIntervalId = null;
    }
  };
}

function useSharedCityWeather(city) {
  const [, bump] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    ensureCityPolling(city);
    const unsubCity = subscribeCity(city, bump);
    const unsubTime = subscribeTimeTick(bump);
    return () => {
      unsubCity();
      unsubTime();
    };
  }, [city]);
  return weatherCache.get(city) || {};
}

const cityIdSlug = (city) => String(city).replace(/[^a-zA-Z0-9_-]/g, "_");

/** OpenWeather `q` value → folder under `public/animation/` */
const cityToAnimationFolder = (city) => {
  const q = String(city).toLowerCase();
  if (q.includes("paris") || q.includes("france")) return "france";
  if (q.includes("berlin") || q.includes("germany")) return "germany";
  if (q.includes("jakarta") || q.includes("indonesia")) return "indonesia";
  return "indonesia";
};

const animationUrl = (folder, file) => getAnimationVideoUrl(folder, file);

const getLocalHour = (weather) => {
  const utcHours = new Date().getUTCHours();
  const offsetH = (weather.timezone || 0) / 3600;
  let h = utcHours + offsetH;
  if (h >= 24) h -= 24;
  if (h < 0) h += 24;
  return h;
};

/** Paris / Berlin style clips (folders `france`, `germany`) */
const pickEuStyleVideo = (weather, folder) => {
  const now = new Date();
  const sunriseTime = new Date(weather.sys.sunrise * 1000);
  const sunsetTime = new Date(weather.sys.sunset * 1000);
  const localHour = getLocalHour(weather);
  const { main } = weather.weather[0];
  const description = (weather.weather[0].description || "").toLowerCase();
  const isOvercast = description.includes("overcast");

  let timeOfDay;
  if (now >= sunriseTime && now <= sunsetTime) {
    timeOfDay = localHour >= 5 && localHour < 12 ? "Morning" : "Noon";
  } else {
    timeOfDay = "Night";
  }

  switch (main) {
    case "Clear":
      if (timeOfDay === "Morning") return animationUrl(folder, "Clear_Morning.mp4");
      if (timeOfDay === "Noon") return animationUrl(folder, "Clear_Noon.mp4");
      return animationUrl(folder, "Clear_Night.mp4");
    case "Clouds":
      if (timeOfDay === "Morning") {
        return animationUrl(folder, isOvercast ? "Cloudy_Grey.mp4" : "Cloudy_Morning.mp4");
      }
      if (timeOfDay === "Noon") {
        return animationUrl(folder, isOvercast ? "Cloudy_Grey.mp4" : "Cloudy_Noon.mp4");
      }
      return animationUrl(folder, "Cloudy_Night.mp4");
    case "Rain":
    case "Drizzle":
      if (timeOfDay === "Morning" || timeOfDay === "Noon") {
        return animationUrl(folder, "Drizzle_Grey.mp4");
      }
      return animationUrl(folder, "Drizzle_Night.mp4");
    case "Haze":
    case "Mist":
    case "Fog":
      if (timeOfDay === "Morning" || timeOfDay === "Noon") {
        return animationUrl(folder, "Haze_Grey.mp4");
      }
      return animationUrl(folder, "Haze_Night.mp4");
    case "Smoke":
    case "Dust":
    case "Sand":
    case "Ash":
      if (timeOfDay === "Morning" || timeOfDay === "Noon") {
        return animationUrl(folder, "Haze_Grey.mp4");
      }
      return animationUrl(folder, "Haze_Night.mp4");
    case "Squall":
    case "Tornado":
      if (timeOfDay === "Morning" || timeOfDay === "Noon") {
        return animationUrl(folder, "Thunderstorm_Grey.mp4");
      }
      return animationUrl(folder, "Thunderstorm_Night.mp4");
    case "Thunderstorm":
      if (timeOfDay === "Morning" || timeOfDay === "Noon") {
        return animationUrl(folder, "Thunderstorm_Grey.mp4");
      }
      return animationUrl(folder, "Thunderstorm_Night.mp4");
    case "Snow":
      if (timeOfDay === "Morning" || timeOfDay === "Noon") {
        return animationUrl(folder, "Snow_Grey.mp4");
      }
      return animationUrl(folder, "Snow_Cloudy_Night.mp4");
    default:
      if (timeOfDay === "Morning") return animationUrl(folder, "Cloudy_Morning.mp4");
      if (timeOfDay === "Noon") return animationUrl(folder, "Cloudy_Noon.mp4");
      return animationUrl(folder, "Cloudy_Night.mp4");
  }
};

/** Jakarta style clips in `indonesia/` */
const pickIndonesiaVideo = (weather) => {
  const folder = "indonesia";
  const localTimeHour = getLocalHour(weather);
  const { main } = weather.weather[0];

  switch (main) {
    case "Clear":
      if (localTimeHour >= 6 && localTimeHour < 12) {
        return animationUrl(folder, "Morning_Clear_1080.mp4");
      }
      if (localTimeHour >= 12 && localTimeHour < 18) {
        return animationUrl(folder, "Noon_Clear_1080.mp4");
      }
      return animationUrl(folder, "Night_Clear_1080.mp4");
    case "Clouds":
      if (localTimeHour >= 6 && localTimeHour < 12) {
        return animationUrl(folder, "Morning_Cloudy_1080.mp4");
      }
      if (localTimeHour >= 12 && localTimeHour < 18) {
        return animationUrl(folder, "Noon_Cloudy_1080.mp4");
      }
      return animationUrl(folder, "Night_Cloudy_1080.mp4");
    case "Rain":
      if (localTimeHour >= 6 && localTimeHour < 12) {
        return animationUrl(folder, "Morning_Noon_Rain_1080.mp4");
      }
      if (localTimeHour >= 12 && localTimeHour < 18) {
        return animationUrl(folder, "Morning_Noon_Rain_1080.mp4");
      }
      return animationUrl(folder, "Night_Rain_1080.mp4");
    case "Haze":
      if (localTimeHour >= 6 && localTimeHour < 12) {
        return animationUrl(folder, "Morning_Haze_1080.mp4");
      }
      if (localTimeHour >= 12 && localTimeHour < 18) {
        return animationUrl(folder, "Noon_Haze_1080.mp4");
      }
      return animationUrl(folder, "Night_Haze_1080.mp4");
    case "Thunderstorm":
      if (localTimeHour >= 6 && localTimeHour < 12) {
        return animationUrl(folder, "Morning_Noon_Thunderstorm_1080.mp4");
      }
      if (localTimeHour >= 12 && localTimeHour < 18) {
        return animationUrl(folder, "Morning_Noon_Thunderstorm_1080.mp4");
      }
      return animationUrl(folder, "Night_Thunderstorm_1080.mp4");
    default:
      if (localTimeHour >= 6 && localTimeHour < 12) {
        return animationUrl(folder, "Morning_Cloudy_1080.mp4");
      }
      if (localTimeHour >= 12 && localTimeHour < 18) {
        return animationUrl(folder, "Noon_Cloudy_1080.mp4");
      }
      return animationUrl(folder, "Night_Cloudy_1080.mp4");
  }
};

/** Showcase palette: cream / paper, gold, warm ink — used for audio glyphs on dark overlays. */
export function IconSpeakerOn({ size = 22 }) {
  const uid = useId().replace(/:/g, "");
  const gBody = `spk-on-body-${uid}`;
  const gWave = `spk-on-wave-${uid}`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <defs>
        <linearGradient id={gBody} x1="2" y1="5" x2="14" y2="20" gradientUnits="userSpaceOnUse">
          <stop stopColor="#faf6f1" />
          <stop offset="1" stopColor="#dfd2c4" />
        </linearGradient>
        <linearGradient id={gWave} x1="15" y1="8" x2="22" y2="18" gradientUnits="userSpaceOnUse">
          <stop stopColor="#e8d4b8" />
          <stop offset="0.55" stopColor="#d4b896" />
          <stop offset="1" stopColor="#b8956c" />
        </linearGradient>
      </defs>
      <path
        d="M11.25 4.75 6.1 8.85H2.75v6.3h3.35l5.15 4.1v-14.5Z"
        fill={`url(#${gBody})`}
        stroke="#c4a574"
        strokeWidth="1.35"
        strokeLinejoin="round"
      />
      <path
        d="M15.75 8.35a4.25 4.25 0 0 1 0 7.3"
        stroke={`url(#${gWave})`}
        strokeWidth="1.55"
        strokeLinecap="round"
      />
      <path
        d="M18.35 6.15a7.2 7.2 0 0 1 0 11.7"
        stroke={`url(#${gWave})`}
        strokeWidth="1.25"
        strokeLinecap="round"
        opacity="0.82"
      />
    </svg>
  );
}

export function IconSpeakerMuted({ size = 22 }) {
  const uid = useId().replace(/:/g, "");
  const gBody = `spk-off-body-${uid}`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <defs>
        <linearGradient id={gBody} x1="2" y1="6" x2="12" y2="19" gradientUnits="userSpaceOnUse">
          <stop stopColor="#c4b8ae" />
          <stop offset="1" stopColor="#8a7b70" />
        </linearGradient>
      </defs>
      <path
        d="M11.25 4.75 6.1 8.85H2.75v6.3h3.35l5.15 4.1v-14.5Z"
        fill={`url(#${gBody})`}
        stroke="#6d6258"
        strokeWidth="1.35"
        strokeLinejoin="round"
      />
      <path
        d="M15.2 8.4 22.25 15.45M22.25 8.4 15.2 15.45"
        stroke="#d4b896"
        strokeWidth="1.65"
        strokeLinecap="round"
        opacity="0.92"
      />
    </svg>
  );
}

const Weather = ({
  city = "Jakarta",
  preview = false,
  immersive = false,
  onArtworkClick,
  muted: mutedProp,
  onMutedChange,
  hideAudioButton = false,
  /** When true, preview is portaled into `lightboxStageRef` (same video element, continuous playback). */
  dockExpanded = false,
  lightboxStageRef = null,
  /** Bumps when the lightbox stage mounts so the portal target exists. */
  lightboxLayoutTick = 0,
}) => {
  const weather = useSharedCityWeather(city);
  const controlledMute = mutedProp !== undefined;
  const [internalMuted, setInternalMuted] = useState(() => (immersive ? false : true));
  const muted = controlledMute ? Boolean(mutedProp) : internalMuted;
  const [immersiveFit, setImmersiveFit] = useState(null);
  const videoRef = useRef(null);
  const jarHostRef = useRef(null);
  const [jarHostReady, setJarHostReady] = useState(false);
  /** Restore time/audio after portal moves the player between card and lightbox. */
  const playbackPreserveRef = useRef(null);

  const folder = cityToAnimationFolder(city);

  const effectiveImmersive = immersive || (preview && dockExpanded);
  void lightboxLayoutTick;

  const idSuffix = preview || immersive ? cityIdSlug(city) : null;

  const getWeatherImage = () => {
    if (!weather.weather || !weather.weather[0]) return undefined;
    if (folder === "indonesia") return pickIndonesiaVideo(weather);
    return pickEuStyleVideo(weather, folder);
  };

  const videoSrc = getWeatherImage();
  const videoId = preview
    ? `weather-image-${idSuffix}`
    : immersive
      ? `weather-image-immersive-${idSuffix}`
      : "weather-image";

  const computeImmersiveFit = useCallback(() => {
    const el = videoRef.current;
    if (!effectiveImmersive || !el) return;
    const iw = el.videoWidth;
    const ih = el.videoHeight;
    if (!iw || !ih) return;
    const ar = iw / ih;
    /* Leave room for lightbox chrome (title bar, padding) so panel does not clip. */
    const chrome = 80;
    const maxW = Math.min(window.innerWidth * 0.94, 1260);
    const maxH = Math.max(120, window.innerHeight * 0.92 - chrome);
    let w = maxW;
    let h = w / ar;
    if (h > maxH) {
      h = maxH;
      w = h * ar;
    }
    setImmersiveFit({ w: Math.floor(w), h: Math.floor(h) });
  }, [effectiveImmersive]);

  useLayoutEffect(() => {
    if (!effectiveImmersive) {
      setImmersiveFit(null);
      return;
    }
    setImmersiveFit(null);
    if (!videoSrc) return;
    const el = videoRef.current;
    if (el && el.readyState >= 1) {
      computeImmersiveFit();
    }
  }, [effectiveImmersive, videoSrc, computeImmersiveFit]);

  useEffect(() => {
    if (!effectiveImmersive) {
      setImmersiveFit(null);
      return undefined;
    }
    const onResize = () => computeImmersiveFit();
    window.addEventListener("resize", onResize);
    const id = window.requestAnimationFrame(() => computeImmersiveFit());
    return () => {
      window.removeEventListener("resize", onResize);
      window.cancelAnimationFrame(id);
    };
  }, [effectiveImmersive, computeImmersiveFit, videoSrc]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !videoSrc || playbackPreserveRef.current) return;
    el.muted = muted;
    if (el.paused) {
      const p = el.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => {});
      }
    }
  }, [videoSrc, muted, dockExpanded, lightboxLayoutTick]);

  useLayoutEffect(() => {
    const saved = playbackPreserveRef.current;
    if (!saved) return undefined;
    const el = videoRef.current;
    if (!el) return undefined;
    playbackPreserveRef.current = null;
    el.currentTime = saved.time;
    el.muted = muted;
    if (saved.playing && !muted) {
      const p = el.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => {});
      }
    }
    return undefined;
  }, [dockExpanded, lightboxLayoutTick, muted]);

  useLayoutEffect(() => {
    const el = videoRef.current;
    return () => {
      if (!dockExpanded || !el) return;
      playbackPreserveRef.current = {
        time: el.currentTime,
        playing: !el.paused && !el.muted,
      };
    };
  }, [dockExpanded, lightboxLayoutTick]);

  const wrapStyle = effectiveImmersive
    ? {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "auto",
        maxWidth: "100%",
        height: "auto",
        minHeight: 0,
        minWidth: 0,
        overflow: "hidden",
        background: "transparent",
      }
    : preview
      ? {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          height: "100%",
          minHeight: 0,
          position: "relative",
          overflow: "hidden",
          borderRadius: "inherit",
        }
      : {
          position: "relative",
          width: "100%",
          height: "100%",
          minHeight: 0,
          minWidth: 0,
          overscrollBehaviorY: "none",
          overscrollBehavior: "none",
          overflow: "hidden",
        };

  const applyMuted = (next) => {
    if (!controlledMute) setInternalMuted(next);
    if (onMutedChange) onMutedChange(next);
  };

  const toggleMute = () => {
    applyMuted(!muted);
  };

  const savePlaybackForPortal = () => {
    const el = videoRef.current;
    if (!el) return;
    playbackPreserveRef.current = {
      time: el.currentTime,
      playing: !el.paused,
    };
  };

  const handleVideoClick = (e) => {
    if (preview && onArtworkClick && !dockExpanded) {
      e.stopPropagation();
      savePlaybackForPortal();
      onArtworkClick();
      return;
    }
    toggleMute();
  };

  const iconSize = preview && !dockExpanded ? 18 : effectiveImmersive ? 24 : 22;
  const btnPad = preview && !dockExpanded ? 6 : effectiveImmersive ? 12 : 10;
  const btnCorner = preview && !dockExpanded ? 6 : effectiveImmersive ? 16 : 14;

  const audioBtnStyle = {
    position: "absolute",
    top: btnCorner,
    right: btnCorner,
    zIndex: 3,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: btnPad,
    border: muted ? "1px solid rgba(118, 104, 94, 0.55)" : "1px solid rgba(212, 184, 150, 0.42)",
    borderRadius: "50%",
    cursor: "pointer",
    color: "#f5f0e8",
    background: muted
      ? "linear-gradient(155deg, rgba(28, 24, 22, 0.88) 0%, rgba(16, 14, 12, 0.74) 100%)"
      : "linear-gradient(155deg, rgba(34, 29, 25, 0.82) 0%, rgba(18, 15, 13, 0.7) 100%)",
    boxShadow: muted
      ? "0 2px 14px rgba(0,0,0,0.32), inset 0 1px 0 rgba(255,255,255,0.03)"
      : "0 2px 16px rgba(0,0,0,0.28), 0 0 0 1px rgba(212, 184, 150, 0.1), inset 0 1px 0 rgba(255,250,245,0.07)",
    backdropFilter: "blur(6px)",
    WebkitBackdropFilter: "blur(6px)",
    transition: "background 0.25s, border-color 0.25s, box-shadow 0.25s, transform 0.15s",
  };

  const handleVideoLoadedData = (e) => {
    const el = e.currentTarget;
    el.muted = muted;
    if (!playbackPreserveRef.current && el.paused) {
      const p = el.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => {});
      }
    }
    if (effectiveImmersive) computeImmersiveFit();
  };

  const handleVideoLoadedMetadata = () => {
    if (effectiveImmersive) computeImmersiveFit();
  };

  const handleVideoCanPlay = () => {
    if (effectiveImmersive) computeImmersiveFit();
  };

  const rootClass = [
    "Weather",
    preview && "Weather--preview",
    effectiveImmersive && "Weather--immersive",
  ]
    .filter(Boolean)
    .join(" ");

  const immersiveShellStyle = immersiveFit
    ? { position: "relative", width: immersiveFit.w, height: immersiveFit.h }
    : {
        position: "relative",
        /* Tight guess until metadata — avoids a full-viewport-wide flash vs object-fit: contain */
        width: "min(56vmin, min(92vw, 640px))",
        aspectRatio: "1",
        maxHeight: "calc(85vh - 80px)",
        marginLeft: "auto",
        marginRight: "auto",
      };

  const immersiveVideoStyle = {
    width: "100%",
    height: "100%",
    display: "block",
    objectFit: "contain",
    objectPosition: "center",
    cursor: "pointer",
  };

  const portalTarget =
    preview && jarHostReady && jarHostRef.current
      ? dockExpanded && lightboxStageRef && lightboxStageRef.current
        ? lightboxStageRef.current
        : jarHostRef.current
      : null;

  const previewShellStyle = effectiveImmersive
    ? immersiveShellStyle
    : {
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: 0,
      };

  const previewVideoStyle = effectiveImmersive
    ? immersiveVideoStyle
    : {
        width: "100%",
        height: "100%",
        objectFit: "cover",
        objectPosition: "center",
        display: "block",
        cursor: dockExpanded
          ? "pointer"
          : onArtworkClick
            ? "zoom-in"
            : "pointer",
      };

  let inner;
  if (preview) {
    inner = (
      <div style={previewShellStyle}>
        <CrossfadeVideo
          src={videoSrc}
          muted={muted}
          videoRef={videoRef}
          loop
          playsInline
          preload={effectiveImmersive ? "metadata" : undefined}
          className="weather-image"
          id={videoId}
          onLoadedData={handleVideoLoadedData}
          onLoadedMetadata={handleVideoLoadedMetadata}
          onCanPlay={effectiveImmersive ? handleVideoCanPlay : undefined}
          onClick={handleVideoClick}
          style={previewVideoStyle}
        />
        {!hideAudioButton ? (
          <button
            type="button"
            style={audioBtnStyle}
            aria-label={muted ? "Unmute video" : "Mute video"}
            title={muted ? "Unmute" : "Mute"}
            onClick={(e) => {
              e.stopPropagation();
              toggleMute();
            }}
          >
            {muted ? (
              <IconSpeakerMuted size={iconSize} />
            ) : (
              <IconSpeakerOn size={iconSize} />
            )}
          </button>
        ) : null}
      </div>
    );
  } else if (effectiveImmersive) {
    inner = (
      <div style={immersiveShellStyle}>
        <CrossfadeVideo
          src={videoSrc}
          muted={muted}
          videoRef={videoRef}
          loop
          playsInline
          preload="metadata"
          className="weather-image"
          id={videoId}
          onLoadedData={handleVideoLoadedData}
          onLoadedMetadata={handleVideoLoadedMetadata}
          onCanPlay={handleVideoCanPlay}
          onClick={handleVideoClick}
          style={immersiveVideoStyle}
        />
        {!hideAudioButton ? (
          <button
            type="button"
            style={audioBtnStyle}
            aria-label={muted ? "Unmute video" : "Mute video"}
            title={muted ? "Unmute" : "Mute"}
            onClick={(e) => {
              e.stopPropagation();
              toggleMute();
            }}
          >
            {muted ? (
              <IconSpeakerMuted size={iconSize} />
            ) : (
              <IconSpeakerOn size={iconSize} />
            )}
          </button>
        ) : null}
      </div>
    );
  } else {
    inner = (
      <>
        <CrossfadeVideo
          src={videoSrc}
          muted={muted}
          videoRef={videoRef}
          loop
          playsInline
          className="weather-image"
          id={videoId}
          onLoadedData={handleVideoLoadedData}
          onLoadedMetadata={handleVideoLoadedMetadata}
          onClick={handleVideoClick}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center",
            display: "block",
            cursor: "pointer",
          }}
        />
        {!hideAudioButton ? (
          <button
            type="button"
            style={audioBtnStyle}
            aria-label={muted ? "Unmute video" : "Mute video"}
            title={muted ? "Unmute" : "Mute"}
            onClick={(e) => {
              e.stopPropagation();
              toggleMute();
            }}
          >
            {muted ? (
              <IconSpeakerMuted size={iconSize} />
            ) : (
              <IconSpeakerOn size={iconSize} />
            )}
          </button>
        ) : null}
      </>
    );
  }

  const shell = (
    <div className={rootClass} style={wrapStyle}>
      {inner}
    </div>
  );

  if (!preview) {
    return shell;
  }

  return (
    <>
      <div
        ref={(node) => {
          jarHostRef.current = node;
          if (node && !jarHostReady) setJarHostReady(true);
        }}
        className="weather-jar-host"
        style={{
          width: "100%",
          height: "100%",
          minHeight: 0,
          visibility: dockExpanded ? "hidden" : "visible",
        }}
        aria-hidden={dockExpanded}
      />
      {portalTarget ? createPortal(shell, portalTarget) : null}
    </>
  );
};

export default Weather;
